/**
 * Dispatcher Job Hook
 *
 * Mirrors Flutter's WizardCubit WebSocket pattern:
 * 1. Connect to dispatcher WebSocket FIRST (no job ID)
 * 2. Send start_job message via WS with action + payload
 * 3. Listen for progress events on the same connection
 * 4. Never miss early events (no race condition)
 * 5. Validate result on completion (hasUsableResult)
 * 6. Auto-cleanup on unmount
 *
 * This replaces the old REST-trigger + WS-listen pattern.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  agentService,
  ProgressEvent,
  hasUsableResult,
} from '../services/agent';

export type DispatcherJobStatus = 'idle' | 'connecting' | 'running' | 'completed' | 'failed';

export interface DispatcherJobState {
  status: DispatcherJobStatus;
  progressMessages: ProgressEvent[];
  currentMessage: string;
  error: string | null;
}

interface UseDispatcherJobOptions {
  /** Called when job completes with a usable result */
  onComplete?: () => void | Promise<void>;
  /** Called when job fails */
  onError?: (error: string) => void;
}

export function useDispatcherJob(options?: UseDispatcherJobOptions) {
  const [state, setState] = useState<DispatcherJobState>({
    status: 'idle',
    progressMessages: [],
    currentMessage: '',
    error: null,
  });

  const wsRef = useRef<WebSocket | null>(null);
  const mountedRef = useRef(true);
  const currentJobActionRef = useRef<string | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close();
      }
    };
  }, []);

  /** Close existing WebSocket connection */
  const closeConnection = useCallback(() => {
    if (wsRef.current) {
      if (wsRef.current.readyState === WebSocket.OPEN ||
          wsRef.current.readyState === WebSocket.CONNECTING) {
        wsRef.current.close();
      }
      wsRef.current = null;
    }
  }, []);

  /**
   * Start a job using the dispatcher pattern (like Flutter).
   *
   * @param jobAction - The job action (e.g., 'build_requirements', 'generate_plan', 'execute_plan')
   * @param payload - The job payload
   */
  const startJob = useCallback(async (
    jobAction: string,
    payload: Record<string, any>
  ) => {
    // Reset state
    setState({
      status: 'connecting',
      progressMessages: [],
      currentMessage: 'Connecting...',
      error: null,
    });

    // Close any existing connection
    closeConnection();
    currentJobActionRef.current = jobAction;

    try {
      // Step 1: Connect to dispatcher FIRST (like Flutter's connectToDispatcher)
      const ws = agentService.connectToDispatcher(
        // onMessage handler
        (event: ProgressEvent) => {
          if (!mountedRef.current) return;

          // Log status values received for debugging (emit_final_result sends status: "complete")
          const jobAction = currentJobActionRef.current;
          const hasResult = hasUsableResult(event.result);
          console.log('[useDispatcherJob] message:', {
            jobAction,
            eventStatus: event.status,
            hasResult,
            resultKeys: event.result && typeof event.result === 'object' ? Object.keys(event.result) : null,
          });

          // Handle completion (with result validation like Flutter)
          // execute_plan: emit_final_result sends { status: "complete", result } - treat as complete even if result is empty.
          const isExecutePlan = jobAction === 'execute_plan';
          const isCompletedStatus = event.status === 'completed' || event.status === 'complete';
          const isRealCompletion = isCompletedStatus && (hasResult || isExecutePlan);
          const isFailed = event.status === 'failed';

          console.log('[useDispatcherJob] completion check:', { isCompletedStatus, isRealCompletion, isFailed, isExecutePlan });

          // Determine effective status for state:
          // Don't set 'completed' unless we have a usable result
          const effectiveStatus: DispatcherJobStatus = isRealCompletion
            ? 'completed'
            : isFailed
              ? 'failed'
              : 'running';

          // Add message to progress log
          setState(prev => ({
            ...prev,
            status: effectiveStatus,
            progressMessages: [...prev.progressMessages, event],
            currentMessage: event.message,
          }));

          if (isRealCompletion) {
            closeConnection();
            if (options?.onComplete) {
              options.onComplete();
            }
          }

          // Handle failure
          if (event.status === 'failed') {
            closeConnection();
            setState(prev => ({
              ...prev,
              error: event.message || 'Job failed',
            }));
            if (options?.onError) {
              options.onError(event.message || 'Job failed');
            }
          }
        },
        // onError handler
        (error) => {
          if (!mountedRef.current) return;
          const errorMsg = typeof error === 'string' ? error : 'WebSocket connection error';
          setState(prev => ({
            ...prev,
            status: 'failed',
            error: errorMsg,
          }));
          if (options?.onError) {
            options.onError(errorMsg);
          }
        },
        // onClose handler
        () => {
          // WebSocket closed - if still running, it means unexpected closure
          if (mountedRef.current) {
            setState(prev => {
              if (prev.status === 'running' || prev.status === 'connecting') {
                return {
                  ...prev,
                  error: 'Connection closed unexpectedly',
                };
              }
              return prev;
            });
          }
        }
      );

      wsRef.current = ws;

      // Step 2: Send start_job via WS (like Flutter's sendStartJob)
      await agentService.sendStartJob(ws, jobAction, payload);

      if (mountedRef.current) {
        setState(prev => ({
          ...prev,
          status: 'running',
          currentMessage: 'Job started via dispatcher',
        }));
      }
    } catch (error: any) {
      if (mountedRef.current) {
        const errorMsg = error?.message || 'Failed to start job';
        setState(prev => ({
          ...prev,
          status: 'failed',
          error: errorMsg,
        }));
        if (options?.onError) {
          options.onError(errorMsg);
        }
      }
    }
  }, [closeConnection, options]);

  /** Cancel the current job (close connection) */
  const cancel = useCallback(() => {
    closeConnection();
    setState(prev => ({
      ...prev,
      status: 'idle',
      currentMessage: 'Cancelled',
    }));
  }, [closeConnection]);

  /** Reset to idle state */
  const reset = useCallback(() => {
    closeConnection();
    setState({
      status: 'idle',
      progressMessages: [],
      currentMessage: '',
      error: null,
    });
  }, [closeConnection]);

  return {
    ...state,
    startJob,
    cancel,
    reset,
    isRunning: state.status === 'running' || state.status === 'connecting',
    isComplete: state.status === 'completed',
    isFailed: state.status === 'failed',
  };
}
